import { Keyboard } from "@capacitor/keyboard"
import type { Platform } from "./detect"

function setKeyboardHeight(height: number) {
	document.documentElement.style.setProperty("--keyboard-height", `${height}px`)
}

export function initKeyboardTracking(platform: Platform): void {
	if (platform === "telegram") {
		// Telegram Mini App: Telegram SDK already shrinks --tg-viewport-height,
		// which drives --app-height. No separate keyboard tracking needed.
		return
	}

	if (platform === "ios") {
		// On iOS with Capacitor (resize: "none"), the WKWebView doesn't resize,
		// so window.innerHeight - visualViewport.height always returns 0.
		// Use native Keyboard events which provide the real keyboard height.
		Keyboard.addListener("keyboardWillShow", (info) => {
			setKeyboardHeight(info.keyboardHeight)
		})
		Keyboard.addListener("keyboardWillHide", () => {
			setKeyboardHeight(0)
		})
		return
	}

	if (platform === "android") {
		// Android: the OS resizes the web viewport natively, --keyboard-height
		// stays 0 and no JS tracking is needed.
		//
		// - API < 35 (Android ≤ 14): windowSoftInputMode="adjustResize" in
		//   AndroidManifest.xml shrinks the window to the top of the IME.
		//   This is the only exact source on these versions: with adjustNothing
		//   the visualViewport never shrinks (regardless of WebView version),
		//   and the native ime() inset is screen-relative, so it over-reports
		//   by the system navigation bar height (verified on-device, see
		//   https://developer.android.com/develop/ui/views/layout/software-keyboard).
		// - API 35+ (Android 15, edge-to-edge enforced): adjustResize is ignored,
		//   Capacitor's SystemBars plugin pads the WebView by the IME inset
		//   instead (see SystemBars.java in @capacitor/android).
		//   https://developer.android.com/develop/ui/views/layout/edge-to-edge
		return
	}

	// browser: track keyboard via visualViewport API.
	// window.innerHeight = layout viewport (stable), visualViewport.height
	// shrinks as the keyboard slides in; the difference is the keyboard height.
	const vv = window.visualViewport
	if (!vv) return

	const update = () => {
		setKeyboardHeight(Math.max(0, window.innerHeight - vv.height))
	}

	vv.addEventListener("resize", update)
	window.addEventListener("resize", update)
}
